import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "../lib/http/axiosConfig";
import {
  dismissMissionStopReturningToast,
  showMissionCompletedToast,
  showMissionReturningToast,
  showMissionStopReadyToast,
  showMissionStopReturningToast,
} from "../utils/missionStoppedToast";
import {
  latchMissionResult,
  resetMissionProgress,
  setConnected,
  setControl,
  setInfoConnected,
  setMissionReturning,
  setSelectedDrone,
  updateDroneData,
} from "../features/DroneSlice";
import {
  setRouteConfirmed,
  setSelectedCellIds,
  setWaypoints,
} from "../features/RouteSlice";
import { clearDemoCellSelection, writeDemoCellSelection } from "./demoCellSelection";
import { listDemoSections } from "./demoSections";
import {
  demoScheduleFireKey,
  isDemoScheduleDue,
  listDemoSchedules,
} from "./demoSchedules";
import { flightOrder } from "../utils/flightOrder";
import { useT } from "../i18n";
import { normalizeInfoEnergyDevices } from "../utils/energyDevices";
import { DEFAULT_DEMO_CROP, buildCellResult, localizeResults } from "./demoAnalysis";
import {
  loadDemoMissionLogs,
  saveDemoMissionLogs,
  sortDemoMissionLogs,
} from "./demoMissionLogs";
import { demoEnergyDevicesInfo, normalizeDemoEnergyConfig } from "./demoDevices";

const DemoScenarioContext = createContext(null);

// users.username has a DB-level unique constraint. Only these reserved accounts
// take the local demo path; every other account continues to use the APIs.
// 계정이 구성을 정한다.
//   demo  : 양배추 밭, 스마트폴 1대, 스트리밍 창 있음. 촬영 회차는 브라우저에 저장(새로고침해도 남음).
//   demo2 : 벼 밭, 큐브 fog(fog 1 + edge 2), 스트리밍 없음. 촬영 회차는 저장하지 않음(새로고침하면 사라짐).
// 전에는 계정 하나에 정보 탭 스위치로 바꿨는데, 시연 중 눌러 바뀌는 것보다 계정으로 고정하는 게 낫다(10-21).
const DEMO_ACCOUNTS = {
  demo: { crop: "cabbage", energy: "POLE", stream: true, persistLogs: true },
  demo2: { crop: "rice", energy: "FOG", stream: false, persistLogs: false },
};
const DEMO_DRONE_ID = 99001;
const CAPTURE_INTERVAL_MS = 5000;
const ANALYSIS_DELAY_MS = 5000;
// 칸 미리보기에 들어가는 사진 장수는 칸 등급이 정한다(CELL_PROFILES).
const RETURN_DURATION_MS = ANALYSIS_DELAY_MS;
const MANUAL_RETURN_DURATION_MS = 10000;
const FLIGHT_TICK_MS = 120;
// 예약은 분 단위라 15초 틱이면 그 분 안에 반드시 한 번은 걸린다.
// 탭이 백그라운드로 눌리면 브라우저가 이 틱을 늦춰 그 분을 통째로 놓칠 수 있다 —
// 서버 예약과 다른 점이고, 구조상 브라우저에서는 못 고친다.
const SCHEDULE_TICK_MS = 15000;
// 섹션에 고도가 없을 때. SectionPanel 의 기본값과 같다.
const SCHEDULE_FALLBACK_ALTITUDE = 4;
// 배터리 자연 감소. 전원이 켜져 있는 동안만 5분에 1% 씩 줄어든다.
// 새로고침하면 처음 값으로 돌아간다 — 저장해 두면 시연을 반복할수록 바닥에 눌어붙는다.
const BATTERY_DRAIN_INTERVAL_MS = 5 * 60 * 1000;
const BATTERY_DRAIN_STEP = 1;
// 칸 하나를 찍을 때마다 추가로 줄어드는 양. 비행이 전원만 켜 둔 것보다 빨리 닳는 걸 보인다.
const BATTERY_CAPTURE_COST = 2;
// 바닥. 더 내려가면 전원 버튼이 잠긴다(Mission 의 powerDisabled).
const BATTERY_FLOOR = 20;

// 미션 상태머신에서 "아직 도는 중" 인 단계들.
// 정상 완주는 mission 을 null 로 만들지 않고 phase 만 "complete" 로 바꾼다(수동 중단만 null).
// 그래서 "mission 이 있는가" 로 비행 여부를 물으면 한 번 날린 뒤로는 영원히 비행 중이 된다.
const ACTIVE_MISSION_PHASES = ["flying", "processing", "returning"];
export const isDemoMissionActive = (mission) =>
  ACTIVE_MISSION_PHASES.includes(mission?.phase);

const formatClock = (seconds) => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = String(Math.floor(total / 3600)).padStart(2, "0");
  const m = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
};

const toCoordinate = (center) => {
  const latitude = Number(center?.latitude ?? center?.lat);
  const longitude = Number(center?.longitude ?? center?.lng);
  return Number.isFinite(latitude) && Number.isFinite(longitude)
    ? { latitude, longitude }
    : null;
};

export const getDemoHomePosition = (gridCells = []) => {
  const centers = gridCells.map((cell) => toCoordinate(cell?.center)).filter(Boolean);
  if (!centers.length) return null;

  const centroid = centers.reduce(
    (sum, point) => ({
      latitude: sum.latitude + point.latitude / centers.length,
      longitude: sum.longitude + point.longitude / centers.length,
    }),
    { latitude: 0, longitude: 0 }
  );
  const edge = centers.reduce((best, point) =>
    point.longitude > best.longitude ? point : best
  );
  const latOffset = edge.latitude - centroid.latitude;
  const lngOffset = edge.longitude - centroid.longitude;

  return {
    latitude: centroid.latitude + (Math.abs(latOffset) + Math.abs(lngOffset) > 0 ? latOffset * 1.24 : 0.00008),
    longitude: centroid.longitude + (Math.abs(latOffset) + Math.abs(lngOffset) > 0 ? lngOffset * 1.24 : 0.00008),
  };
};

const interpolateCoordinate = (from, to, progress) => ({
  latitude: from.latitude + (to.latitude - from.latitude) * progress,
  longitude: from.longitude + (to.longitude - from.longitude) * progress,
});

const bearingTo = (from, to) => {
  const y = to.longitude - from.longitude;
  const x = to.latitude - from.latitude;
  return (Math.atan2(y, x) * 180) / Math.PI;
};


const DEMO_INFO = {
  farm: {
    // name/location/cropName 은 화면 문구라 노출 시점(demoInfo useMemo)에 언어별로 채운다.
    farmSize: 10788.09,
    cellCount: 220,
  },
  // 드론 이름·모델은 관리자에 등록된 그 농장 드론(info 응답 droneInfo)에서 온다(demoInfo). 등록 드론이 없으면
  // 정보 탭에 드론 카드를 그리지 않는다. 아래 droneName 은 테스트용 값. id 는 비행 시뮬레이션·지도 마커 키라 99001 고정.
  drone: {
    id: DEMO_DRONE_ID,
    droneName: "TB_Drone",
    powerStatus: false,
    armingStatus: false,
    missionStatus: false,
    batteryCharge: 88,
    gpsStatus: 6,
    isConnected: true,
  },
  // 에너지 장치(스마트폴 · 태양광큐브)는 demo/demoDevices.js 하나에서 나온다.
  // 구성(energyConfig)과 지도 좌표(gridCells)에 따라 달라져서 demoInfo useMemo 에서 채운다.
};

// 홈 복귀가 끝나면 지도에 남은 경로선/웨이포인트 선택을 정리한다.
// 미션이 끝나면 선택을 비운다. 실계정은 서버가 비우므로 새로고침해도 비어 있다.
// 데모의 선택은 브라우저에 있으니 저장소도 같이 비워야 새로고침 뒤 옛 선택이 되살아나지 않는다.
const clearDemoRoute = (dispatch, farmId) => {
  dispatch(setWaypoints([]));
  dispatch(setSelectedCellIds([]));
  dispatch(setRouteConfirmed(false));
  clearDemoCellSelection(farmId);
};

// 실계정은 드론 전원·ARM 이 서버에 있어서 새로고침해도 /dashboard/info 가 되살린다.
// 데모는 그 서버가 없으므로 같은 자리를 브라우저가 맡는다.
// missionStatus 는 일부러 안 되살린다 — 비행을 굴리던 타이머는 새로고침으로 이미 사라졌고,
// 상태만 "비행 중" 으로 남으면 시작도 중단도 안 되는 화면이 된다.
const DEMO_CONTROL_STORAGE_KEY = "demo-drone-control";

const loadDemoControl = () => {
  try {
    const raw = window.localStorage.getItem(DEMO_CONTROL_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== "object") return null;
    return {
      powerStatus: Boolean(parsed.powerStatus),
      armingStatus: Boolean(parsed.armingStatus),
    };
  } catch {
    return null;
  }
};

const saveDemoControl = (controls) => {
  try {
    window.localStorage.setItem(
      DEMO_CONTROL_STORAGE_KEY,
      JSON.stringify({
        powerStatus: Boolean(controls?.powerStatus),
        armingStatus: Boolean(controls?.armingStatus),
      })
    );
  } catch {
    // 저장 실패는 시연을 막지 않는다.
  }
};

const clearTimers = (timers) => {
  timers.forEach((timer) => clearTimeout(timer));
  timers.length = 0;
};

export function DemoScenarioProvider({ children }) {
  const t = useT();
  const dispatch = useDispatch();
  const location = useLocation();
  const gridCells = useSelector((state) => state.farm.gridCells);
  // 밭 경계 다각형. 데모 큐브를 이 경계의 변 위에 세운다(demoCubeRow).
  const boundary = useSelector((state) => state.farm.boundary);
  // 미션 콜백이 농장 변경마다 새로 만들어지면 돌고 있는 타이머가 정리된다. 최신 값만 참조한다.
  const farmId = useSelector((state) => state.farm.farmId);
  const farmIdRef = useRef(farmId);
  const controls = useSelector((state) => state.drone.control);
  const droneData = useSelector((state) => state.drone.droneData);
  const timersRef = useRef([]);
  const accessToken = localStorage.getItem("accessToken") ?? "";
  const [resolvedAccessToken, setResolvedAccessToken] = useState("");
  const [profile, setProfile] = useState(null);
  const [mission, setMission] = useState(null);
  // 복귀까지 끝난 미션 기록. 실계정의 mission-log 대용이며 하루에 여러 건 쌓인다.
  // 농장이 정해진 뒤에 그 농장의 기록을 읽어 채운다(아래 effect).
  const [missionLogs, setMissionLogs] = useState([]);
  // 저장하지 않는 계정(persistLogs=false)은 미션이 끝날 때 저장소 대신 이 ref 의 메모리 목록에 이어 붙인다.
  const missionLogsRef = useRef([]);
  missionLogsRef.current = missionLogs;
  // 비행 시간/남은 시간 표시용 경과 초 (1초 단위 갱신)
  const [elapsedSec, setElapsedSec] = useState(0);
  // 비행이 끝난 뒤에도 남겨 둘 마지막 비행 시간. 다음 미션을 시작할 때만 0 으로 내린다.
  // (진행 링의 latchMissionResult 와 같은 생각 — 끝났다고 값을 지우면 방금 얼마나 날았는지
  //  확인할 틈이 없다.)
  const [lastFlightSec, setLastFlightSec] = useState(0);
  // 화면에 떠 있는 비행 시간. 완주든 중단이든 "마지막으로 보여 준 값" 을 그대로 물려주려고 둔다.
  const liveFlightSecRef = useRef(0);
  const isAuthPage = ["/login", "/register", "/recovery", "/privacy"].includes(location.pathname);

  useEffect(() => {
    farmIdRef.current = farmId;
  }, [farmId]);
  const profileReady = !accessToken || resolvedAccessToken === accessToken;

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      if (!accessToken) {
        setProfile(null);
        setResolvedAccessToken("");
        return;
      }

      setProfile(null);
      setResolvedAccessToken(null);
      try {
        const { data } = await axios.get("/api/users/me");
        if (!cancelled) setProfile(data?.data ?? null);
      } catch {
        if (!cancelled) setProfile(null);
      } finally {
        if (!cancelled) setResolvedAccessToken(accessToken);
      }
    };

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [accessToken, location.key]);

  const demoAccount = profile?.username ? DEMO_ACCOUNTS[profile.username] : undefined;
  const isDemo = Boolean(demoAccount);
  // 데모 스트리밍 창(mp4)을 띄울지. demo2 는 끈다.
  const demoStream = Boolean(demoAccount?.stream);
  // 샘플 사진·병명·작물명 세트(demoAnalysis / demoDashboard / i18n demo.farm.cropName.*).
  const demoCrop = demoAccount?.crop ?? DEFAULT_DEMO_CROP;
  // 촬영 회차를 브라우저(localStorage)에 남길지. demo2 는 메모리에만 두어 새로고침하면 사라진다.
  const persistLogs = demoAccount?.persistLogs !== false;

  // 관리자에 등록된 이 농장의 에너지 장치(이름·시리얼·역할·좌표). 데모는 값만 가짜고 장치 자체는
  // 관리자 메뉴에서 맞춘다 — 하드코딩한 이름을 쓰지 않는다. 못 받으면 null → 프로필 폴백(demoDevices).
  // 한 번만 읽는다. 정보 탭과 같은 이유로 주기 호출은 하지 않는다(토큰 재발급 순간의 401 → 리마운트).
  const [registeredDevices, setRegisteredDevices] = useState(null);
  // 같은 응답의 droneInfo(id · droneName · modelName). 데모 드론의 이름·모델만 여기서 가져온다.
  const [registeredDrone, setRegisteredDrone] = useState(null);
  useEffect(() => {
    if (!isDemo || !farmId) {
      setRegisteredDevices(null);
      setRegisteredDrone(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`/api/dashboard/${farmId}/info`);
        if (cancelled) return;
        const info = data?.data;
        setRegisteredDevices(normalizeInfoEnergyDevices(info));
        setRegisteredDrone(info?.droneInfo ?? null);
      } catch {
        if (!cancelled) {
          setRegisteredDevices(null);
          setRegisteredDrone(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isDemo, farmId]);

  useEffect(() => {
    if (!isDemo || mission) return;
    const home = getDemoHomePosition(gridCells);
    if (!home) return;
    dispatch(
      updateDroneData({
        latitude: home.latitude,
        longitude: home.longitude,
        altitude: 0,
        heading: 0,
        power_status: controls.powerStatus,
        arming_status: controls.armingStatus,
        streaming: "OFF",
        battery_charge: batteryRef.current,
      })
    );
  }, [controls.armingStatus, controls.powerStatus, dispatch, gridCells, isDemo, mission]);

  // 데모 진입 시 딱 한 번만 초기화한다.
  //
  // 예전에는 isDemo 가 바뀔 때마다 돌았는데, 토큰이 재발급되면
  // accessToken -> 프로필 재조회 -> profile=null -> isDemo 가 true→false→true 로 왕복한다.
  // 그때마다 이 블록이 다시 돌아 전원을 OFF 로 되돌리고(=비행 불가),
  // cleanup 의 clearTimers 가 비행 중이던 미션 타이머까지 죽였다.
  const demoInitializedRef = useRef(false);
  useEffect(() => {
    if (!isDemo || demoInitializedRef.current) return;
    demoInitializedRef.current = true;

    const saved = loadDemoControl();
    dispatch(setSelectedDrone(DEMO_DRONE_ID));
    dispatch(setConnected(true));
    dispatch(
      setControl({
        powerStatus: saved?.powerStatus ?? false,
        armingStatus: saved?.armingStatus ?? false,
        missionStatus: false,
        serialNum: "DEMO-DRONE-01",
        gpsStatus: 6,
      })
    );
    dispatch(
      updateDroneData({
        battery_charge: batteryRef.current,
        battery_voltage: 15.8,
        waypoint_done: 0,
        waypoint_total: 0,
        waypoint_percent: 0,
        streaming: "OFF",
      })
    );
  }, [dispatch, isDemo]);

  // 타이머 정리는 provider 가 정말 사라질 때만. isDemo 왕복에 딸려 돌면 비행이 끊긴다.
  useEffect(() => () => clearTimers(timersRef.current), []);

  // 전원·ARM 이 바뀔 때마다 남겨 둔다. 새로고침 뒤에도 켜 둔 드론은 켜져 있어야 한다.
  useEffect(() => {
    if (!isDemo) return;
    saveDemoControl(controls);
  }, [isDemo, controls]);

  // ── 예약 발화 ────────────────────────────────────────────────────
  // 실계정은 BE 스케줄러가 예약 시각에 비행을 건다. 데모는 그 서버가 없으므로
  // 여기서 현재 시각을 읽어 직접 건다. 화면이 닫혀 있으면 돌지 않는다.
  const missionRef = useRef(null);
  useEffect(() => {
    missionRef.current = mission;
  }, [mission]);
  // 같은 분 안에서 틱이 여러 번 깨어나므로 이미 쏜 예약을 기억해 둔다.
  const firedSchedulesRef = useRef(new Set());

  // ── 배터리 ──────────────────────────────────────────────────────
  // 한 곳에서만 값을 들고 있는다. 예전에는 화면마다 "88 - 완료칸*2" 를 따로 계산해서
  // 미션이 끝나면 다시 88% 로 돌아가 있었다(시연 내내 배터리가 안 줄어 보였다).
  // 타이머에서도 읽어야 하므로 ref 를 같이 두고, 화면 갱신용으로만 state 를 쓴다.
  const batteryRef = useRef(DEMO_INFO.drone.batteryCharge);
  const [batteryPercent, setBatteryPercent] = useState(DEMO_INFO.drone.batteryCharge);
  // 에너지 장치 구성은 계정이 정한다(DEMO_ACCOUNTS). 데모가 아니면 쓰이지 않는다.
  const energyConfig = normalizeDemoEnergyConfig(demoAccount?.energy);
  const drainBattery = useCallback(
    (amount) => {
      const next = Math.max(BATTERY_FLOOR, batteryRef.current - amount);
      if (next === batteryRef.current) return next;
      batteryRef.current = next;
      setBatteryPercent(next);
      dispatch(updateDroneData({ battery_charge: next }));
      return next;
    },
    [dispatch]
  );
  const drainBatteryRef = useRef(drainBattery);
  useEffect(() => {
    drainBatteryRef.current = drainBattery;
  }, [drainBattery]);

  // 전원이 켜져 있는 동안만 닳는다. 꺼 두면 멈춘다.
  const poweredOn = controls.powerStatus === true;

  // 데모에는 서버 하트비트가 없다. 전원이 곧 연결 여부다 —
  // 실계정에서 드론 전원을 끄면 하트비트가 끊겨 infoConnected 가 false 가 되는 것과 같은 결과를 만든다.
  // 이 값 하나로 미션 탭의 배터리·GPS·비행 시간·상태 줄과 지도의 비행 노트 배지가 함께 움직인다.
  useEffect(() => {
    if (!isDemo) return;
    dispatch(setInfoConnected(poweredOn));
  }, [dispatch, isDemo, poweredOn]);
  useEffect(() => {
    if (!isDemo || !poweredOn) return undefined;
    const timer = setInterval(
      () => drainBatteryRef.current(BATTERY_DRAIN_STEP),
      BATTERY_DRAIN_INTERVAL_MS
    );
    return () => clearInterval(timer);
  }, [isDemo, poweredOn]);

  const fireSchedule = useCallback(
    (schedule) => {
      // 미션은 한 번에 하나만 돈다. 비행 중이면 이번 예약은 버린다(다음 주에 다시 온다).
      // 끝난 미션도 객체는 남아 있으므로 존재 여부가 아니라 단계를 본다.
      if (isDemoMissionActive(missionRef.current)) {
        toast.info(t("scheduler.schedule.demo.skippedBusy"), {
          toastId: "demo-schedule-busy",
        });
        return;
      }
      const farmId = farmIdRef.current;
      const section =
        schedule.sectionId == null
          ? null
          : listDemoSections(farmId).find(
              (item) => String(item.id) === String(schedule.sectionId)
            ) ?? null;
      // 구역을 안 고른 예약은 실계정과 같이 전체 칸을 훑는다.
      const baseIds = section?.cellIds?.length
        ? section.cellIds
        : (gridCells ?? []).map((cell) => cell.id);
      // 지도 경로선·웨이포인트와 같은 함수로 줄 세운다.
      const ordered = flightOrder(baseIds, gridCells);
      if (!ordered.length) return;

      const altitude = Number(section?.altitude) || SCHEDULE_FALLBACK_ALTITUDE;
      const cellMap = new Map((gridCells ?? []).map((cell) => [cell.id, cell]));
      const waypoints = ordered
        .map((id, index) => {
          const center = cellMap.get(id)?.center;
          if (!center) return null;
          return {
            id,
            seq: index + 1,
            gridCellId: id,
            latitude: center.latitude,
            longitude: center.longitude,
            altitude,
          };
        })
        .filter(Boolean);

      // 손으로 시작할 때 DroneControl 이 하는 일과 같은 상태를 만들어 준다.
      // 이걸 빼면 미션은 도는데 사이드바의 "비행 구역" 과 지도 경로선이 비어 보인다.
      dispatch(setSelectedCellIds(ordered));
      writeDemoCellSelection(farmId, ordered);
      dispatch(setWaypoints(waypoints));

      if (!startDemoMissionRef.current(ordered)) return;
      toast.success(
        t("scheduler.schedule.demo.fired", {
          name: section?.name ?? t("scheduler.schedule.demo.allCells"),
          count: ordered.length,
          // 칸당 촬영 5초 + 분석 5초. 전체 칸 예약이 몇 분짜리인지 눌러 보기 전에 알린다.
          minutes: Math.max(
            1,
            Math.round((ordered.length * CAPTURE_INTERVAL_MS + ANALYSIS_DELAY_MS) / 60000)
          ),
        })
      );
    },
    [dispatch, gridCells, t]
  );

  const fireScheduleRef = useRef(fireSchedule);
  useEffect(() => {
    fireScheduleRef.current = fireSchedule;
  }, [fireSchedule]);

  useEffect(() => {
    if (!isDemo) return undefined;
    const timer = setInterval(() => {
      const now = new Date();
      const due = listDemoSchedules(now).find((schedule) => {
        if (!isDemoScheduleDue(schedule, now)) return false;
        return !firedSchedulesRef.current.has(demoScheduleFireKey(schedule, now));
      });
      if (!due) return;
      firedSchedulesRef.current.add(demoScheduleFireKey(due, now));
      fireScheduleRef.current(due);
    }, SCHEDULE_TICK_MS);
    return () => clearInterval(timer);
  }, [isDemo]);

  const startDemoMission = useCallback(
    (cellIds) => {
      if (!isDemo || !Array.isArray(cellIds) || cellIds.length === 0) return false;
      // 새 비행이 시작되는 이 자리에서만 지난 비행 시간을 내린다.
      setLastFlightSec(0);
      liveFlightSecRef.current = 0;
      const selectedIds = [...cellIds];
      const startedAt = new Date();
      const id = `demo-${startedAt.getTime()}`;
      const cellMap = new Map(
        gridCells.map((cell) => [cell.id, toCoordinate(cell.center)])
      );
      const route = selectedIds.map((cellId) => cellMap.get(cellId)).filter(Boolean);
      if (route.length !== selectedIds.length) return false;
      const home = getDemoHomePosition(gridCells) ?? route[0];
      // 칸마다 등급 프로필대로 사진을 채운다. 무작위가 아니라 순번 기준이라
      // 같은 칸을 다시 열어도, 새로고침해도 같은 화면이 나온다(캡처 재현성).
      const results = selectedIds.map((cellId, index) =>
        buildCellResult(cellId, index, startedAt, CAPTURE_INTERVAL_MS, index, demoCrop)
      );

      // 미션 전체 소요 시간 — 시작 시 1회 계산해 고정한다.
      // 예상 종료 = 이 값, 남은 시간 = 이 값에서 1초씩 카운트다운.
      const processingAt = results.length * CAPTURE_INTERVAL_MS + 400;
      const durationSec = Math.round((processingAt + ANALYSIS_DELAY_MS) / 1000);

      clearTimers(timersRef.current);
      setMission({
        id,
        startedAt,
        completedAt: null,
        phase: "flying",
        total: results.length,
        completed: 0,
        durationSec,
        results,
        home,
      });
      dispatch(resetMissionProgress(results.length));
      dispatch(
        setControl({
          powerStatus: true,
          armingStatus: true,
          missionStatus: true,
          serialNum: "DEMO-DRONE-01",
          gpsStatus: 6,
        })
      );
      dispatch(setMissionReturning(false));
      dispatch(
        updateDroneData({
          latitude: home.latitude,
          longitude: home.longitude,
          altitude: 0,
          heading: bearingTo(home, route[0]),
          power_status: true,
          arming_status: true,
          // 데모는 실제 송출이 없다. 켜 두면 StreamFloat 가 없는 주소로 iframe 을 띄운다.
          streaming: "OFF",
        })
      );
      results.forEach((_, index) => {
        timersRef.current.push(
          setTimeout(() => {
            const completed = index + 1;
            setMission((current) =>
              current?.id === id
                ? { ...current, completed, phase: "flying" }
                : current
            );
            drainBatteryRef.current(BATTERY_CAPTURE_COST);
            dispatch(
              updateDroneData({
                waypoint_done: completed,
                waypoint_total: results.length,
                waypoint_percent: Math.round((completed / results.length) * 100),
                flight_time_sec: completed * 3,
                time_left_sec: Math.max(0, (results.length - completed) * 3),
              })
            );
          }, (index + 1) * CAPTURE_INTERVAL_MS)
        );
      });

      const flightStartedAt = Date.now();
      const routePoints = [home, ...route];
      const flightTimer = setInterval(() => {
        const elapsed = Date.now() - flightStartedAt;
        const legIndex = Math.min(
          Math.floor(elapsed / CAPTURE_INTERVAL_MS),
          routePoints.length - 2
        );
        const legProgress = Math.min(
          1,
          (elapsed - legIndex * CAPTURE_INTERVAL_MS) / CAPTURE_INTERVAL_MS
        );
        const from = routePoints[legIndex];
        const to = routePoints[legIndex + 1];
        const position = interpolateCoordinate(from, to, legProgress);
        dispatch(
          updateDroneData({
            ...position,
            altitude: 36,
            heading: bearingTo(from, to),
            power_status: true,
            arming_status: true,
          })
        );
      }, FLIGHT_TICK_MS);
      timersRef.current.push(flightTimer);

      timersRef.current.push(
        setTimeout(() => {
          clearInterval(flightTimer);
          setMission((current) =>
            current?.id === id ? { ...current, phase: "processing" } : current
          );
          dispatch(setMissionReturning(true));
          const returnStartedAt = Date.now();
          const returnFrom = routePoints.at(-1);
          const returnTimer = setInterval(() => {
            const progress = Math.min(1, (Date.now() - returnStartedAt) / RETURN_DURATION_MS);
            const position = interpolateCoordinate(returnFrom, home, progress);
            dispatch(
              updateDroneData({
                ...position,
                altitude: Math.round((1 - progress) * 36),
                heading: bearingTo(returnFrom, home),
                power_status: true,
                arming_status: progress < 1,
              })
            );
            if (progress >= 1) clearInterval(returnTimer);
          }, FLIGHT_TICK_MS);
          timersRef.current.push(returnTimer);
          showMissionReturningToast();
        }, processingAt)
      );

      timersRef.current.push(
        setTimeout(() => {
          const completedAt = new Date();
          setMission((current) =>
            current?.id === id
              ? { ...current, phase: "complete", completed: results.length, completedAt }
              : current
          );
          dispatch(
            setControl({
              powerStatus: true,
              armingStatus: false,
              missionStatus: false,
              serialNum: "DEMO-DRONE-01",
              gpsStatus: 6,
            })
          );
          dispatch(setMissionReturning(false));
          dispatch(
            updateDroneData({
              waypoint_done: results.length,
              waypoint_total: results.length,
              waypoint_percent: 100,
              time_left_sec: 0,
              latitude: home.latitude,
              longitude: home.longitude,
              altitude: 0,
              heading: bearingTo(routePoints.at(-1), home),
              power_status: true,
              arming_status: false,
              streaming: "OFF",
            })
          );
          // 실계정의 complete 프레임과 같은 자리. 이걸 해 둬야 착륙 뒤에도 진행 링이
          // N/N 으로 남고, 새 업로드(clearMissionResult)에서 0/선택 수로 내려간다.
          dispatch(latchMissionResult({ total: results.length }));
          // 진행 링과 같이 비행 시간도 붙잡아 둔다.
          setLastFlightSec(liveFlightSecRef.current);
          // 저장과 화면 상태를 같은 자리에서 맞춘다. 저장을 effect 로 미루면
          // 농장을 바꾸는 순간 옛 기록이 새 농장 칸에 덮어써진다.
          // 저장소를 먼저 읽는 이유: 다른 탭에서 날린 회차가 있어도 덮지 않는다.
          // 저장 안 하는 계정은 지금 메모리에 있는 목록에만 이어 붙인다.
          const logFarmId = farmIdRef.current;
          const nextLogs = sortDemoMissionLogs([
            ...(persistLogs ? loadDemoMissionLogs(logFarmId) : missionLogsRef.current),
            { id, completedAt, results, total: results.length },
          ]);
          if (persistLogs) saveDemoMissionLogs(logFarmId, nextLogs);
          setMissionLogs(nextLogs);
          clearDemoRoute(dispatch, farmIdRef.current);
          toast.dismiss("mission-returning");
          showMissionCompletedToast();
        }, processingAt + ANALYSIS_DELAY_MS)
      );

      return true;
    },
    [demoCrop, dispatch, gridCells, isDemo, persistLogs]
  );

  const startDemoMissionRef = useRef(startDemoMission);
  useEffect(() => {
    startDemoMissionRef.current = startDemoMission;
  }, [startDemoMission]);

  const stopDemoMission = useCallback(() => {
    if (!isDemo) return;
    clearTimers(timersRef.current);
    const home = getDemoHomePosition(gridCells);
    if (!home) return;
    const current = toCoordinate(droneData) ?? home;
    const currentAltitude = Math.max(0, Number(droneData?.altitude) || 36);
    setMission((active) => (active ? { ...active, phase: "returning" } : active));
    dispatch(
      setControl({
        powerStatus: true,
        armingStatus: true,
        missionStatus: false,
        serialNum: "DEMO-DRONE-01",
        gpsStatus: 6,
      })
    );
    dispatch(setMissionReturning(true));
    dispatch(
      updateDroneData({
        ...current,
        altitude: currentAltitude,
        power_status: true,
        arming_status: true,
        // 데모는 실제 송출이 없다. 켜 두면 StreamFloat 가 없는 주소로 iframe 을 띄운다.
        streaming: "OFF",
      })
    );
    showMissionStopReturningToast();

    const returnStartedAt = Date.now();
    const returnTimer = setInterval(() => {
      const progress = Math.min(1, (Date.now() - returnStartedAt) / MANUAL_RETURN_DURATION_MS);
      const position = interpolateCoordinate(current, home, progress);
      dispatch(
        updateDroneData({
          ...position,
          altitude: Math.round((1 - progress) * currentAltitude),
          heading: bearingTo(current, home),
          power_status: true,
          arming_status: progress < 1,
          streaming: "OFF",
        })
      );
      if (progress < 1) return;

      clearInterval(returnTimer);
      // 중간에 세웠어도 그때까지 날린 시간은 남긴다.
      setLastFlightSec(liveFlightSecRef.current);
      setMission(null);
      dispatch(
        setControl({
          powerStatus: true,
          armingStatus: false,
          missionStatus: false,
          serialNum: "DEMO-DRONE-01",
          gpsStatus: 6,
        })
      );
      dispatch(setMissionReturning(false));
      clearDemoRoute(dispatch, farmIdRef.current);
      dismissMissionStopReturningToast();
      showMissionStopReadyToast();
    }, FLIGHT_TICK_MS);
    timersRef.current.push(returnTimer);
  }, [dispatch, droneData, gridCells, isDemo]);

  // 비행 시간/남은 시간을 1초 단위로 갱신한다.
  // - flying/processing 동안만 tick, 정지 후 복귀(returning) 중에는 값을 고정
  // - 완료 시엔 총 소요 시간에 맞춰 딱 떨어지게 마감
  const missionId = mission?.id ?? null;
  const missionPhase = mission?.phase ?? null;
  const missionDurationSec = mission?.durationSec ?? 0;
  const missionStartedMs = mission?.startedAt?.getTime() ?? 0;

  useEffect(() => {
    if (!isDemo || !missionId) {
      setElapsedSec(0);
      return undefined;
    }
    if (missionPhase === "complete") {
      setElapsedSec(missionDurationSec);
      return undefined;
    }
    if (missionPhase !== "flying" && missionPhase !== "processing") return undefined;

    const tick = () =>
      setElapsedSec(Math.max(0, Math.floor((Date.now() - missionStartedMs) / 1000)));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [isDemo, missionDurationSec, missionId, missionPhase, missionStartedMs]);

  useEffect(() => {
    if (!isDemoMissionActive(mission)) return;
    liveFlightSecRef.current = Math.min(elapsedSec, mission?.durationSec ?? 0);
  }, [mission, elapsedSec]);

  const demoDroneOverview = useMemo(() => {
    const total = mission?.total ?? 0;
    const completed = mission?.completed ?? 0;
    const durationSec = mission?.durationSec ?? 0;
    // 비행 중에는 시계가 흐르고, 착륙하면 그 자리에서 멈춰 그대로 남는다.
    // 0 으로 내리는 시점은 "다음 미션 시작" 한 곳뿐이다(startDemoMission).
    const flying = isDemoMissionActive(mission);
    const flightSec = flying && mission
      ? Math.min(elapsedSec, durationSec)
      : lastFlightSec;
    const timeLeft = flying ? Math.max(0, durationSec - flightSec) : 0;
    return {
      droneName: registeredDrone?.droneName ?? "",
      battery: { percent: batteryPercent, voltageV: 15.8 },
      waypointProgress: {
        completed,
        total,
        percent: total ? Math.round((completed / total) * 100) : 0,
      },
      timing: {
        // 예상 종료 = 미션 시작 시 확정된 총 소요 시간. 미션 동안 고정.
        eta: formatClock(durationSec),
        flightTimeSec: formatClock(flightSec),
        timeLeftSec: formatClock(timeLeft),
      },
    };
  }, [batteryPercent, elapsedSec, lastFlightSec, mission, registeredDrone]);

  // 보고 있는 농장의 기록을 읽어 온다. 농장을 바꾸면 그 농장 것으로 갈아끼운다.
  // 저장은 미션이 끝나는 자리에서 직접 한다(saveDemoMissionLogs). 저장 안 하는 계정은 빈 목록에서 시작한다.
  useEffect(() => {
    if (!isDemo || !persistLogs) {
      setMissionLogs([]);
      return;
    }
    setMissionLogs(loadDemoMissionLogs(farmId));
  }, [isDemo, farmId, persistLogs]);

  // 컨텍스트 밖으로 나가는 값은 현재 언어의 문구가 붙은 상태로 만든다.
  // (저장 상태는 언어 중립 키만 갖고, 언어가 바뀌면 여기서 다시 계산된다.)
  const localizedMission = useMemo(
    () => (mission ? { ...mission, results: localizeResults(mission.results, t) } : mission),
    [mission, t]
  );
  const localizedMissionLogs = useMemo(
    () => missionLogs.map((log) => ({ ...log, results: localizeResults(log.results, t) })),
    [missionLogs, t]
  );
  const demoInfo = useMemo(
    () => ({
      ...DEMO_INFO,
      // 이름·모델은 등록 드론, 상태(전원·배터리·비행)는 데모 시뮬레이션 값. 등록 드론이 없으면 카드 없음(null).
      drone: registeredDrone
        ? {
            ...DEMO_INFO.drone,
            droneName: registeredDrone.droneName,
            modelName: registeredDrone.modelName,
            batteryCharge: batteryPercent,
          }
        : null,
      farm: {
        ...DEMO_INFO.farm,
        name: t("demo.farm.name"),
        location: t("demo.farm.location"),
        cropName: t(`demo.farm.cropName.${demoCrop}`),
      },
      // 실계정 info 응답의 energyDevices 와 같은 모양. 장치는 관리자 등록값, 값은 프로필.
      // 큐브는 밭 경계의 긴 변 위에 한 줄(fog 가운데), 폴은 드론 홈.
      energyDevices: demoEnergyDevicesInfo(energyConfig, getDemoHomePosition(gridCells), {
        centers: (gridCells ?? []).map((cell) => toCoordinate(cell?.center)).filter(Boolean),
        boundary,
        registered: registeredDevices,
      }),
    }),
    [batteryPercent, boundary, demoCrop, energyConfig, gridCells, registeredDevices, registeredDrone, t]
  );

  const getDemoResultForCell = useCallback(
    (cellId, missionLogId = null) => {
      const log = missionLogId
        ? localizedMissionLogs.find((item) => item.id === missionLogId)
        : null;
      const results =
        log?.results ??
        (localizedMission?.phase === "complete" ? localizedMission.results : null);
      return results?.find((result) => result.cellId === cellId) ?? null;
    },
    [localizedMission, localizedMissionLogs]
  );

  const value = useMemo(
    () => ({
      profileReady,
      isDemo,
      demoStream,
      demoCrop,
      demoInfo,
      demoRegisteredDevices: registeredDevices,
      energyConfig,
      mission: localizedMission,
      missionLogs: localizedMissionLogs,
      demoDroneOverview,
      startDemoMission,
      stopDemoMission,
      getDemoResultForCell,
    }),
    [
      demoDroneOverview,
      demoCrop,
      demoInfo,
      demoStream,
      registeredDevices,
      energyConfig,
      getDemoResultForCell,
      isDemo,
      localizedMission,
      localizedMissionLogs,
      profileReady,
      startDemoMission,
      stopDemoMission,
    ]
  );

  return (
    <DemoScenarioContext.Provider value={value}>
      {!isAuthPage && !profileReady ? (
        <div className="app-loading-screen">
          <img src={`${process.env.PUBLIC_URL || ""}/loading.gif`} alt={t("demo.loading.alt")} />
        </div>
      ) : children}
    </DemoScenarioContext.Provider>
  );
}

export function useDemoScenario() {
  const value = useContext(DemoScenarioContext);
  if (value == null) {
    throw new Error("useDemoScenario must be used inside DemoScenarioProvider");
  }
  return value;
}
